import { LICENSE_FLOW_META_QUERY_RESULT, LICENSE_FLOW_QUERY_RESULT } from '@koda/koda-types/sanity/corporateTypes';
import {
  BACKGROUND_THEATER,
  BAR_AND_NIGHTLIFE,
  CAMPSITE,
  CHOIR,
  CHURCH,
  CINEMA,
  CITY_AND_MARKET_FESTIVAL,
  COMPANY_PARTY,
  CONCERT_AND_EVENT,
  DANCE_SCHOOL,
  FILM_SCREENING,
  FITNESS_CENTER,
  FOOTBALL,
  GYMNASTICS_PERFORMANCE,
  HANDBALL,
  HOTEL,
  ICE_HOCKEY_AND_VOLLEY,
  LOCAL_RADIO,
  MUSEUM_AND_GALLERY,
  MUSIC_FOR_EMPLOYEES,
  MUSIC_ON_PHONE_LINES,
  MUSIC_ON_WEBSITE,
  NATIONAL_RADIO,
  ONLINE_STREAMING_DOWNLOAD_NETRADIO,
  OTHER_CONTACT_FORM,
  PODCAST,
  REGIONAL_RADIO,
  RESTAURANT_AND_CAFE,
  RUNNING_EVENT,
  SCHOOL_AND_EDUCATION,
  SENIOR_CENTER,
  SPORTS_AND_ATHLETICS_EVENT,
  STORE_AND_SERVICE,
  THEATER,
  TRADE_FAIRS,
  TV,
} from '@koda/koda-utils/constants/licenses';
import { z } from 'zod';

export const LicenseTypes = {
  ShopAndServiceIndustry: STORE_AND_SERVICE,
  RestaurantAndCafe: RESTAURANT_AND_CAFE,
  BarClubAndNightlife: BAR_AND_NIGHTLIFE,
  HotelAndAccommodation: HOTEL,
  ConcertAndEvent: CONCERT_AND_EVENT,
  Cinema: CINEMA,
  FilmScreening: FILM_SCREENING,
  Theater: THEATER,
  BackgroundTheater: BACKGROUND_THEATER,
  CityAndMarketFestival: CITY_AND_MARKET_FESTIVAL,
  CampingSite: CAMPSITE,
  Church: CHURCH,
  Choir: CHOIR,
  MuseumAndGallery: MUSEUM_AND_GALLERY,
  FitnessCenterAndTreatment: FITNESS_CENTER,
  Football: FOOTBALL,
  DanceSchool: DANCE_SCHOOL,
  Gymnastics: GYMNASTICS_PERFORMANCE,
  IceHockeyAndVolleyball: ICE_HOCKEY_AND_VOLLEY,
  Handball: HANDBALL,
  SportsEventsAndTournaments: SPORTS_AND_ATHLETICS_EVENT,
  RecreationalRuns: RUNNING_EVENT,
  LocalRadio: LOCAL_RADIO,
  RegionalRadio: REGIONAL_RADIO,
  NationalRadio: NATIONAL_RADIO,
  Television: TV,
  Podcast: PODCAST,
  MusicOnWebsite: MUSIC_ON_WEBSITE,
  Online: ONLINE_STREAMING_DOWNLOAD_NETRADIO,
  MusicOnPhoneLines: MUSIC_ON_PHONE_LINES,
  MusicForEmployees: MUSIC_FOR_EMPLOYEES,
  CompanyParty: COMPANY_PARTY,
  TradeFairs: TRADE_FAIRS,
  SchoolAndEducation: SCHOOL_AND_EDUCATION,
  SeniorCenter: SENIOR_CENTER,
  OtherContactForm: OTHER_CONTACT_FORM,
} as const;

const LICENSES_WITH_VENUE_ADDRESS = [
  LicenseTypes.ConcertAndEvent,
  LicenseTypes.CompanyParty,
  LicenseTypes.RecreationalRuns,
  LicenseTypes.SportsEventsAndTournaments,
  LicenseTypes.Football,
  LicenseTypes.Handball,
  LicenseTypes.IceHockeyAndVolleyball,
  LicenseTypes.Gymnastics,
] as const;

const LICENSES_WITH_NO_ADDRESS = [
  LicenseTypes.Podcast,
  LicenseTypes.MusicOnPhoneLines,
  LicenseTypes.OtherContactForm,
  LicenseTypes.BackgroundTheater,
  LicenseTypes.Theater,
] as const;

const SPORTS_LICENSE_CODES = [
  LicenseTypes.Football,
  LicenseTypes.Handball,
  LicenseTypes.IceHockeyAndVolleyball,
  LicenseTypes.RecreationalRuns,
  LicenseTypes.Gymnastics,
  LicenseTypes.SportsEventsAndTournaments,
] as const;

const LICENSES_WITH_EXISTING_CUSTOMERS = [
  LicenseTypes.ConcertAndEvent,
  LicenseTypes.Handball,
  LicenseTypes.Football,
  LicenseTypes.IceHockeyAndVolleyball,
  LicenseTypes.DanceSchool,
  LicenseTypes.Cinema,
] as const;

export const validateLicenseCode = (code: string | null): LicenseCode => {
  if (Object.values(LicenseTypes).includes(code as LicenseCode)) {
    return code as LicenseCode;
  }
  throw new Error(`Invalid license code: ${code}`);
};

export type LicenseKey = keyof typeof LicenseTypes;
export type LicenseCode = (typeof LicenseTypes)[LicenseKey];

export const LICENSE_CODES = Object.values(LicenseTypes) as LicenseCode[];

export const getLicenseKeyByCode = (currentLicenseCode: LicenseCode | null): LicenseKey | undefined => {
  if (!currentLicenseCode) return undefined;
  return (Object.keys(LicenseTypes) as Array<keyof typeof LicenseTypes>).find(
    key => LicenseTypes[key] === currentLicenseCode,
  );
};

export const hasVenueAddressSection = (licenseCode: string | null | undefined): boolean => {
  if (!licenseCode) return false;
  return (LICENSES_WITH_VENUE_ADDRESS as readonly string[]).includes(licenseCode);
};

export const hasNoAddressSection = (licenseCode: string | null | undefined): boolean => {
  if (!licenseCode) return false;
  return (LICENSES_WITH_NO_ADDRESS as readonly string[]).includes(licenseCode);
};

export const askIfExistingCustomer = (licenseCode: string | null | undefined): boolean => {
  if (!licenseCode) return false;
  return (LICENSES_WITH_EXISTING_CUSTOMERS as readonly string[]).includes(licenseCode);
};

export const isSportsLicense = (licenseCode: string | null | undefined): boolean => {
  if (!licenseCode) return false;
  return (SPORTS_LICENSE_CODES as readonly string[]).includes(licenseCode);
};

export const LicensePlanSchema = z.object({
  classesPriceExclTax: z.number().optional(),
  arrangementPriceExclTax: z.number().optional(),
  priceExclTax: z.number(),
  priceInclTax: z.number(),
  recurringPeriodPrice: z.number(),
  recurringPeriodPriceInclTax: z.number(),
  recurringPeriodTimeFactor: z.string().nullable(),
});

export type LicensePlan = z.infer<typeof LicensePlanSchema>;
export type MetaLicense = NonNullable<NonNullable<LICENSE_FLOW_META_QUERY_RESULT>>;
export type SanityLicense = NonNullable<NonNullable<LICENSE_FLOW_QUERY_RESULT>>;

export type CustomerCategory = { id: number | null; name: string | null };
type WareNumber = number;

export type LicenseInformation = {
  wareNumbers: WareNumber[] | null;
  licenseCode: LicenseCode;
  categories: CustomerCategory[] | null;
};
